import React, { useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { CountryCodes, LEADS_TASK_TYPE, strings } from '@constants'
import { Input, Select, Textarea, Button, Calendar, TimePicker, TOAST_TYPE } from '@base'
import { showLoader, showToast } from '@data/state/action'
import { toggleDrawer } from '@data/state/action/root'
import { addLeadTask } from '@data/conn/put/leads'
import { Videocam, People, Phone, Close } from '@material-ui/icons'
import { getNumberWithCountryCode, mobileLength } from '@utils'
import { MEETING_TYPES } from '../MeetingCard'
import {
	FormBody,
	FormFooter,
	FormSection,
	SectionLabel,
	DateTimeRow,
	DateField,
	TimeField,
	MeetingTypeRow,
	MeetingTypeOption,
	PhoneInputRow,
	CountryCodeField,
	PhoneNumberField,
	GuestInputRow,
	GuestChipList,
	GuestChip,
} from './styles'

const MEETING_TYPE_OPTIONS = [
	{ key: MEETING_TYPES.VIDEO_CALL, label: 'VIDEO_CALL', icon: Videocam },
	{ key: MEETING_TYPES.IN_PERSON, label: 'IN_PERSON', icon: People },
	{ key: MEETING_TYPES.PHONE_CALL, label: 'PHONE_CALL', icon: Phone },
]

const TYPE_LABELS = {
	[MEETING_TYPES.VIDEO_CALL]: () => strings(['video', 'call']),
	[MEETING_TYPES.IN_PERSON]: () => strings('in_person'),
	[MEETING_TYPES.PHONE_CALL]: () => strings(['phone', 'call']),
}

const defaultCountryCode = CountryCodes.find((_c) => _c.default)

const epochToTimeString = (epoch) => {
	if (!epoch) return null
	const d = new Date(epoch)
	const hh = String(d.getHours()).padStart(2, '0')
	const mm = String(d.getMinutes()).padStart(2, '0')
	return `${hh}:${mm}`
}

const addMinutesToTimeString = (timeStr, minutes) => {
	if (!timeStr) return null
	const [h, m] = timeStr.split(':').map(Number)
	const total = h * 60 + m + minutes
	const wrapped = ((total % (24 * 60)) + 24 * 60) % (24 * 60)
	const newH = Math.floor(wrapped / 60)
	const newM = wrapped % 60
	return `${String(newH).padStart(2, '0')}:${String(newM).padStart(2, '0')}`
}

const splitMobileNumber = (stored) => {
	if (!stored) return { country: defaultCountryCode, number: '' }
	const match = CountryCodes.find((c) => {
		const dial = c.dial_code?.replace('+', '')?.trim()
		return dial && stored.startsWith(dial)
	})
	const country = match || defaultCountryCode
	const dial = country.dial_code?.replace('+', '')?.trim() || ''
	return { country, number: stored.slice(dial.length) }
}

const deriveState = (data) => {
	const mobile = splitMobileNumber(data?.meeting_mobile_number)
	return {
		title: {
			id: 'meeting-title',
			placeholder: strings('add_title'),
			value: data?.title || '',
			label: strings('meeting_title'),
			required: true,
		},
		date: {
			id: 'meeting-date',
			value: data?.date || null,
			label: strings('date'),
			required: true,
		},
		meeting_start_date_time: {
			id: 'meeting-start-time',
			value: epochToTimeString(data?.meeting_start_date_time),
			label: strings('start_time'),
			required: true,
		},
		meeting_end_date_time: {
			id: 'meeting-end-time',
			value: epochToTimeString(data?.meeting_end_date_time),
			label: strings('end_time'),
			required: true,
		},
		meeting_type: {
			id: 'meeting-type',
			value: data?.meeting_mode || MEETING_TYPES.IN_PERSON,
			label: strings('meeting_type'),
			required: true,
		},
		meeting_location: {
			id: 'meeting-meeting_location',
			placeholder: strings('enter_meeting_location'),
			value: data?.meeting_location || '',
			label: strings('meeting_location'),
			required: true,
		},
		country_code: {
			key: 'country_code',
			value: mobile.country,
			valid: true,
		},
		meeting_mobile_number: {
			id: 'meeting-phone-number',
			placeholder: strings('enter_phone_number'),
			value: mobile.number,
			label: strings('phone_number'),
			required: false,
			valid: null,
		},
		guest_input: {
			id: 'meeting-guests',
			placeholder: strings('guests'),
			value: '',
			label: strings('guests'),
			required: false,
		},
		guests: {
			id: 'meeting-guests-list',
			value: data?.invitees || [],
			label: strings('guests'),
			required: (data?.meeting_mode || MEETING_TYPES.IN_PERSON) !== MEETING_TYPES.PHONE_CALL,
		},
		description: {
			id: 'meeting-description',
			placeholder: strings('add_meeting_agenda_or_notes'),
			value: data?.description || '',
			label: strings('description'),
			required: false,
		},
	}
}

const ScheduleNewMeeting = ({ leadId, update, data, readOnly = false }) => {
	const dispatch = useDispatch()
	const [formState, setFormState] = useState(deriveState(data))

	const updateState = (field, key) => (value) => {
		setFormState((state) => {
			const newState = {
				...state,
				[field]: {
					...state[field],
					[key]: value,
				},
			}
			if (field === 'meeting_start_date_time' && key === 'value') {
				newState.meeting_end_date_time = {
					...newState.meeting_end_date_time,
					value: addMinutesToTimeString(value, 30),
				}
			}
			if (field === 'meeting_type') {
				newState.meeting_location = {
					...newState.meeting_location,
					value: '',
					required: value === MEETING_TYPES.IN_PERSON,
				}
				newState.country_code = {
					...newState.country_code,
					value: defaultCountryCode,
				}
				newState.meeting_mobile_number = {
					...newState.meeting_mobile_number,
					value: '',
					required: value === MEETING_TYPES.PHONE_CALL,
					valid: null,
				}
				newState.guests = {
					...newState.guests,
					value: [],
					required: value !== MEETING_TYPES.PHONE_CALL,
				}
				newState.guest_input = {
					...newState.guest_input,
					value: '',
				}
			}
			return newState
		})
	}

	const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
	const isValidIndianMobile = (number) => /^[6-9]\d{9}$/.test(number)

	const addGuest = () => {
		const value = formState.guest_input.value.trim()
		if (!value) return

		if (formState.meeting_type.value === MEETING_TYPES.VIDEO_CALL) {
			if (!isValidEmail(value)) {
				dispatch(showToast(true, strings('enter_valid_email'), { type: TOAST_TYPE.ERROR }))
				return
			}
		} else {
			if (!isValidIndianMobile(value)) {
				dispatch(showToast(true, strings('pls_enter_valid_mobile_num'), { type: TOAST_TYPE.ERROR }))
				return
			}
		}

		if (!formState.guests.value.includes(value)) {
			updateState('guests', 'value')([...formState.guests.value, value])
			updateState('guest_input', 'value')('')
		}
	}

	const removeGuest = (name) => {
		updateState('guests', 'value')(formState.guests.value.filter((g) => g !== name))
	}

	const handleGuestKeyDown = (e) => {
		if (e.key === 'Enter') {
			e.preventDefault()
			addGuest()
		}
	}

	const disabled = useMemo(() => {
		const anyRequiredEmpty = Object.values(formState).some((d) => {
			if (!d.required) return false
			if (Array.isArray(d.value)) return d.value.length === 0
			return !d.value
		})
		if (anyRequiredEmpty) return true
		if (formState.meeting_type.value === MEETING_TYPES.PHONE_CALL && formState.meeting_mobile_number.valid !== true) return true
		return false
	}, [formState])

	const getTimeEpoch = (timeStr) => {
		if (!timeStr) return null
		const [hours, minutes] = timeStr.split(':').map(Number)
		const dt = new Date(formState.date.value)
		dt.setHours(hours, minutes, 0, 0)
		return dt.getTime()
	}

	const handleSubmit = () => {
		if (disabled) return

		const startEpoch = getTimeEpoch(formState.meeting_start_date_time.value)
		const endEpoch = getTimeEpoch(formState.meeting_end_date_time.value)

		if (startEpoch && startEpoch <= Date.now()) {
			dispatch(showToast(true, 'Meeting cannot be scheduled in the past', { type: TOAST_TYPE.ERROR }))
			return
		}

		if (startEpoch && endEpoch && endEpoch <= startEpoch) {
			dispatch(showToast(true, 'End time must be greater than start time', { type: TOAST_TYPE.ERROR }))
			return
		}

		const meetingData = {
			title: formState.title.value.trim(),
			date: formState.date.value,
			meeting_start_date_time: startEpoch,
			meeting_end_date_time: endEpoch,
			meeting_mode: formState.meeting_type.value,
			meeting_location: formState.meeting_type.value === MEETING_TYPES.IN_PERSON ? formState.meeting_location.value : null,
			meeting_mobile_number:
				formState.meeting_type.value === MEETING_TYPES.PHONE_CALL
					? getNumberWithCountryCode(formState.country_code.value, formState.meeting_mobile_number.value)
					: null,
			invitees: formState.guests.value,
			description: formState.description.value.trim(),
		}

		dispatch(showLoader(true))
		addLeadTask(leadId, { ...meetingData, type: LEADS_TASK_TYPE.MEETING })
			.then(() => {
				dispatch(showToast(true, strings(['meeting', 'scheduled', 'successfully']), { type: TOAST_TYPE.SUCCESS }))
				dispatch(toggleDrawer(false))
				update()
			})
			.catch((err) => {
				dispatch(
					showToast(true, err?.response?.data?.message || strings(['failed', 'to', 'schedule', 'meeting']), { type: TOAST_TYPE.ERROR })
				)
			})
			.finally(() => {
				dispatch(showLoader(false))
			})
	}

	const handleCancel = () => {
		dispatch(toggleDrawer(false))
	}

	return (
		<>
			<FormBody>
				<FormSection>
					<SectionLabel>
						{formState.title.label} <span>*</span>
					</SectionLabel>
					<Input
						id={formState.title.id}
						placeholder={formState.title.placeholder}
						value={formState.title.value}
						onChange={updateState('title', 'value')}
						required={formState.title.required}
						disabled={readOnly}
					/>
				</FormSection>

				<DateTimeRow>
					<DateField>
						<FormSection>
							<SectionLabel>
								{formState.date.label} <span>*</span>
							</SectionLabel>
							<Calendar
								label={formState.date.label}
								value={formState.date.value}
								minDate={new Date()}
								onChange={updateState('date', 'value')}
								disabled={readOnly}
							/>
						</FormSection>
					</DateField>
					<TimeField>
						<FormSection>
							<SectionLabel>
								{formState.meeting_start_date_time.label} <span>*</span>
							</SectionLabel>
							<TimePicker
								label={formState.meeting_start_date_time.label}
								value={formState.meeting_start_date_time.value}
								onChange={updateState('meeting_start_date_time', 'value')}
								disabled={readOnly}
							/>
						</FormSection>
					</TimeField>
					<TimeField>
						<FormSection>
							<SectionLabel>
								{formState.meeting_end_date_time.label} <span>*</span>
							</SectionLabel>
							<TimePicker
								label={formState.meeting_end_date_time.label}
								value={formState.meeting_end_date_time.value}
								onChange={updateState('meeting_end_date_time', 'value')}
								disabled={readOnly}
							/>
						</FormSection>
					</TimeField>
				</DateTimeRow>

				<FormSection>
					<SectionLabel>
						{formState.meeting_type.label} <span>*</span>
					</SectionLabel>
					<MeetingTypeRow>
						{MEETING_TYPE_OPTIONS.map((option) => {
							const Icon = option.icon
							return (
								<MeetingTypeOption
									key={option.key}
									$active={formState.meeting_type.value === option.key}
									$disabled={readOnly}
									onClick={() => !readOnly && updateState('meeting_type', 'value')(option.key)}
								>
									<Icon />
									<span>{TYPE_LABELS[option.key]?.()}</span>
								</MeetingTypeOption>
							)
						})}
					</MeetingTypeRow>
				</FormSection>

				{formState.meeting_type.value === MEETING_TYPES.IN_PERSON && (
					<FormSection>
						<SectionLabel>
							{formState.meeting_location.label} <span>*</span>
						</SectionLabel>
						<Input
							id={formState.meeting_location.id}
							placeholder={formState.meeting_location.placeholder}
							value={formState.meeting_location.value}
							onChange={updateState('meeting_location', 'value')}
							required={formState.meeting_location.required}
							disabled={readOnly}
						/>
					</FormSection>
				)}

				{formState.meeting_type.value === MEETING_TYPES.PHONE_CALL && (
					<FormSection>
						<SectionLabel>
							{formState.meeting_mobile_number.label} <span>*</span>
						</SectionLabel>
						<PhoneInputRow>
							<CountryCodeField>
								<Select
									id='meeting-country-code'
									data={CountryCodes}
									displayKey='dial_code'
									primaryKey='name'
									type='tel'
									noDropIcon
									value={CountryCodes.find((item) => formState.country_code.value?.code === item.code) ?? CountryCodes[0]}
									onChange={(value) => updateState('country_code', 'value')(value)}
									disabled={readOnly}
								/>
							</CountryCodeField>
							<PhoneNumberField>
								<Input
									id={formState.meeting_mobile_number.id}
									type='tel'
									placeholder={formState.meeting_mobile_number.placeholder}
									value={formState.meeting_mobile_number.value}
									pattern='^\d{7}\d+$'
									maxLength={mobileLength(formState.country_code.value?.dial_code)}
									errorMap={{
										patternMismatch: `${strings('enter_valid_num')}`,
									}}
									required={formState.meeting_mobile_number.required}
									disabled={readOnly}
									onChange={updateState('meeting_mobile_number', 'value')}
									hasError={(error) => {
										setFormState((_s) => ({
											..._s,
											meeting_mobile_number: {
												..._s.meeting_mobile_number,
												valid: !error,
											},
										}))
									}}
								/>
							</PhoneNumberField>
						</PhoneInputRow>
					</FormSection>
				)}

				<FormSection>
					<SectionLabel>
						{formState.guests.label} {formState.guests.required && <span>*</span>}
					</SectionLabel>
					{!readOnly && (
						<GuestInputRow>
							<Input
								id={formState.guest_input.id}
								placeholder={
									formState.meeting_type.value === MEETING_TYPES.VIDEO_CALL ? strings('enter_email') : strings('enter_phone_number')
								}
								value={formState.guest_input.value}
								onChange={updateState('guest_input', 'value')}
								onKeyDown={handleGuestKeyDown}
							/>
							<Button small type='secondary' onClick={addGuest} disabled={!formState.guest_input.value.trim()}>
								{strings('add_user')}
							</Button>
						</GuestInputRow>
					)}
					{formState.guests.value.length > 0 && (
						<GuestChipList>
							{formState.guests.value.map((guest) => (
								<GuestChip key={guest}>
									{guest}
									{!readOnly && <Close onClick={() => removeGuest(guest)} />}
								</GuestChip>
							))}
						</GuestChipList>
					)}
				</FormSection>

				<FormSection>
					<SectionLabel>
						{formState.description.label} ({strings('optional')})
					</SectionLabel>
					<Textarea
						id={formState.description.id}
						placeholder={formState.description.placeholder}
						value={formState.description.value}
						onChange={updateState('description', 'value')}
						rows={4}
						disabled={readOnly}
					/>
				</FormSection>
			</FormBody>

			<FormFooter>
				{!readOnly && (
					<Button small type='primary' onClick={handleSubmit} disabled={disabled}>
						{strings('schedule_meeting')}
					</Button>
				)}
				<Button small type='secondary' onClick={handleCancel}>
					{strings('cancel')}
				</Button>
			</FormFooter>
		</>
	)
}

export default ScheduleNewMeeting
